import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import { MobileProvider } from "./contexts/MobileContext";
import { AccountantProvider } from "./contexts/AccountantContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import RolesPage from "./pages/RolesPage";
import ProfilePage from "./pages/ProfilePage";
import CompanyPage from "./pages/CompanyPage";
import ContactsPage from "./pages/ContactsPage";

// Module Pages
import HRPage from "./pages/modules/HRPage";
import AttendancePage from "./pages/modules/AttendancePage";
import AccountsPage from "./pages/modules/AccountsPage";
import JournalEntriesPage from "./pages/modules/JournalEntriesPage";
import LedgerPage from "./pages/modules/LedgerPage";
import AuditLogPage from "./pages/modules/AuditLogPage";
import PeriodLocksPage from "./pages/modules/PeriodLocksPage";
import TrialBalancePage from "./pages/modules/TrialBalancePage";
import FinancialReportsPage from "./pages/modules/FinancialReportsPage";
import StatementOfAccountsPage from "./pages/modules/StatementOfAccountsPage";
import BankReconciliationPage from "./pages/modules/BankReconciliationPage";
import VATInvoiceSummaryPage from "./pages/modules/VATInvoiceSummaryPage";
import SalesPage from "./pages/modules/SalesPage";
import QuotationCostingPage from "./pages/modules/QuotationCostingPage";
import BillingPage from "./pages/BillingPage";
import NotesPage from "./pages/NotesPage";
import PayrollPage from "./pages/modules/PayrollPage";
import PurchasesPage from "./pages/modules/PurchasesPage";
import ApprovalsPage from "./pages/modules/ApprovalsPage";
import JobOrdersPage from "./pages/modules/JobOrdersPage";
import AgreementsPage from "./pages/modules/AgreementsPage";
import SubcontractorAgreementsPage from "./pages/modules/SubcontractorAgreementsPage";
import CreditDebitNotesPage from "./pages/modules/CreditDebitNotesPage";

// Analytics & Intelligence Pages
import AnalyticsDashboardPage from "./pages/modules/AnalyticsDashboardPage";
import ForecastingPage from "./pages/modules/ForecastingPage";
import InsightsPage from "./pages/modules/InsightsPage";
import CostControlPage from "./pages/modules/CostControlPage";

// Compliance
import CompliancePage from "./pages/modules/CompliancePage";
import TaxCompliancePage from "./pages/modules/TaxCompliancePage";

// Storage & Calendar
import StoragePage from "./pages/modules/StoragePage";
import CalendarPage from "./pages/modules/CalendarPage";

// Inventory
import InventoryPage from "./pages/modules/InventoryPage";

// Reports
import ReportsPage from "./pages/modules/ReportsPage";

// Recycle Bin
import RecycleBinPage from "./pages/modules/RecycleBinPage";

// Recovery
import ForgotPasswordPage, { ForgotUsernamePage } from "./pages/ForgotPasswordPage";

// Audit & Backup
import AuditLogsPage from "./pages/modules/AuditLogsPage";

// Security Settings
import SecuritySettingsPage from "./pages/modules/SecuritySettingsPage";

// Payment & Receipt Vouchers
import VouchersPage from "./pages/modules/VouchersPage";

// Deleted Documents
import DeletedDocumentsPage from "./pages/modules/DeletedDocumentsPage";

// Tasks, Chat & Help
import TasksPage from "./pages/modules/TasksPage";
import ChatPage from "./pages/modules/ChatPage";
import HelpPage from "./pages/modules/HelpPage";
import UserGuidePage from "./pages/modules/UserGuidePage";
import BookletPage from "./pages/modules/BookletPage";

// Legal Pages
import { TermsPage, PrivacyPage, DisclaimerPage } from "./pages/LegalPages";

// Accountant Mode (BizCore Pro)
import AccountantDashboard from "./pages/modules/AccountantDashboard";

// Platform Admin
import PlatformAdminPage from "./pages/modules/PlatformAdminPage";

// Backup & Recovery
import BackupRecoveryPage from "./pages/modules/BackupRecoveryPage";

// Landing / Marketing
import LandingPage from "./pages/LandingPage";
import FeaturesPage from "./pages/marketing/FeaturesPage";
import SecurityPage from "./pages/marketing/SecurityPage";
import PricingPage from "./pages/marketing/PricingPage";
import ContactPage from "./pages/marketing/ContactPage";
import SocialPostPage from "./pages/marketing/SocialPostPage";
import { DemoModeBanner } from "./components/DemoModeBanner";

// Layouts
import DashboardLayout from "./components/layout/DashboardLayout";

function App() {
  // Number-input scroll-wheel guard (Feb 2026).
  // Users on Invoice / Quotation / Voucher / payroll etc. accidentally change
  // Qty, Price and Tax values by scrolling the mouse wheel while the field is
  // focused. Blurring the input on wheel keeps the page scrolling normal while
  // preventing the value from being silently mutated. One tiny global handler
  // covers every <Input type="number" /> across the whole app.
  useEffect(() => {
    const onWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number" && e.target === el) {
        el.blur();
      }
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <CompanyProvider>
          <AccountantProvider>
            <MobileProvider>
              <BrowserRouter>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/forgot-username" element={<ForgotUsernamePage />} />
                
                {/* Protected Dashboard Routes */}
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="hr" element={<HRPage />} />
                  <Route path="attendance" element={<AttendancePage />} />
                  <Route path="payroll" element={<PayrollPage />} />
                  <Route path="compliance" element={<CompliancePage />} />
                  <Route path="storage" element={<StoragePage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="inventory" element={<InventoryPage />} />
                  <Route path="job-orders" element={<JobOrdersPage />} />
                  <Route path="job-orders/:id" element={<JobOrdersPage />} />
                  <Route path="agreements" element={<AgreementsPage />} />
                  <Route path="agreements/:id" element={<AgreementsPage />} />
                  <Route path="subcontractor-agreements" element={<SubcontractorAgreementsPage />} />
                  <Route path="subcontractor-agreements/:id" element={<SubcontractorAgreementsPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="recycle-bin" element={<RecycleBinPage />} />
                  <Route path="audit" element={<AuditLogsPage />} />
                  <Route path="security" element={<SecuritySettingsPage />} />
                  <Route path="purchases" element={<PurchasesPage />} />
                  <Route path="approvals" element={<ApprovalsPage />} />
                  <Route path="accounts" element={<AccountsPage />} />
                  <Route path="accounts/journal" element={<JournalEntriesPage />} />
                  <Route path="accounts/ledger" element={<LedgerPage />} />
                  <Route path="accounts/period-locks" element={<PeriodLocksPage />} />
                  <Route path="accounts/audit-trail" element={<AuditLogPage />} />
                  <Route path="accounts/trial-balance" element={<TrialBalancePage />} />
                  <Route path="accounts/reports" element={<FinancialReportsPage />} />
                  <Route path="accounts/statements" element={<StatementOfAccountsPage />} />
                  <Route path="accounts/bank-reconciliation" element={<BankReconciliationPage />} />
                  <Route path="reports/vat-invoice-summary" element={<VATInvoiceSummaryPage />} />
                  <Route path="accounts/tax" element={<TaxCompliancePage />} />
                  <Route path="sales" element={<SalesPage />} />
                  <Route path="sales/quotations/:quotationId/costing" element={<QuotationCostingPage />} />
                  <Route path="notes" element={<CreditDebitNotesPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="billing/success" element={<BillingPage />} />
                  <Route path="notes" element={<NotesPage />} />
                  <Route path="contacts" element={<ContactsPage />} />
                  <Route path="analytics" element={<AnalyticsDashboardPage />} />
                  <Route path="forecasting" element={<ForecastingPage />} />
                  <Route path="insights" element={<InsightsPage />} />
                  <Route path="cost-control" element={<CostControlPage />} />
                  <Route path="vouchers" element={<VouchersPage />} />
                  <Route path="deleted-documents" element={<DeletedDocumentsPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="roles" element={<RolesPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="company" element={<CompanyPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="chat" element={<ChatPage />} />
                  <Route path="guide" element={<UserGuidePage />} />
                  <Route path="booklet" element={<BookletPage />} />
                  <Route path="help" element={<HelpPage />} />
                  {/* Accountant Mode Routes (BizCore Pro) */}
                  <Route path="accountant" element={<AccountantDashboard />} />
                  <Route path="accountant/client/:clientId" element={<AccountantDashboard />} />
                  <Route path="accountant/reports" element={<AccountantDashboard />} />
                  <Route path="accountant/documents" element={<AccountantDashboard />} />
                  {/* Platform Admin Route */}
                  <Route path="platform-admin" element={<PlatformAdminPage />} />
                  {/* Backup & Recovery Route */}
                  <Route path="backup" element={<BackupRecoveryPage />} />
                </Route>
                
                {/* Redirect root to landing page */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/marketing/social-post" element={<SocialPostPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                
                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" />
          </MobileProvider>
          </AccountantProvider>
        </CompanyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
